import { useEffect, useState } from 'react';
import { Area, DiscoveryOffer, getNearbyOffers, getRestaurant, getRestaurants, getWineShop, getWineShops, RestoDto, ShopDto } from '../api';
import { useGeolocation } from './useGeolocation';

type Status = 'loading' | 'success' | 'error';

// Listing hooks: near the visitor, or in the chosen area (State / District filter) when given one.
function useListing<T>(fetch: (lat: number, lng: number, area?: Area) => Promise<T[]>, area?: Area) {
  const { coords, resolved } = useGeolocation();
  const [data, setData] = useState<T[]>([]);
  const [status, setStatus] = useState<Status>('loading');
  const state = area?.state ?? null;
  const district = area?.district ?? null;

  useEffect(() => {
    if (!resolved) return;
    let current = true;
    setStatus('loading');
    fetch(coords.lat, coords.lng, { state, district })
      .then((items) => {
        if (!current) return;
        setData(items);
        setStatus('success');
      })
      .catch(() => current && setStatus('error'));
    return () => {
      current = false;
    };
  }, [fetch, resolved, coords.lat, coords.lng, state, district]);

  return { data, status };
}

export const useRestaurants = (area?: Area) => useListing<RestoDto>(getRestaurants, area);
export const useWineShops = (area?: Area) => useListing<ShopDto>(getWineShops, area);
export const useNearbyOffers = (area?: Area) => useListing<DiscoveryOffer>(getNearbyOffers, area);

// One venue ("resto") or wine shop ("shop") by id, wherever it is; null once loaded means not found.
export function useVenue(kind: string, id: number) {
  const { coords, resolved } = useGeolocation();
  const [venue, setVenue] = useState<RestoDto | ShopDto | null>(null);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    if (!resolved) return;
    if ((kind !== 'resto' && kind !== 'shop') || !id) {
      setVenue(null);
      setStatus('success');
      return;
    }
    let current = true;
    setStatus('loading');
    (kind === 'resto' ? getRestaurant(id, coords.lat, coords.lng) : getWineShop(id, coords.lat, coords.lng))
      .then((v) => current && (setVenue(v), setStatus('success')))
      .catch(() => current && (setVenue(null), setStatus('success')));
    return () => {
      current = false;
    };
  }, [kind, id, resolved, coords.lat, coords.lng]);

  return { venue, status };
}
